/**
 * Device cart persistence for logged-out buyers.
 *
 * The guard style follows src/util/webStorageHelpers.js: `window` is checked on every
 * call rather than once at module load, so this is safe to import from anything that
 * renders on the server. Reads always return an array — a corrupt or missing key is an
 * empty cart, never an exception.
 */

import { normalizeCartRow } from './cart';

export const CART_STORAGE_KEY = 'r2r-cart';

const getLocalStorage = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }
  return window.localStorage;
};

/**
 * @returns {Array<{ listingId: string, qty: number, deliveryMethod: string, unitPriceAtAdd: number|null, addedAt: string|null }>}
 */
export const readStoredCart = () => {
  const storage = getLocalStorage();
  if (!storage) {
    return [];
  }

  try {
    const raw = storage.getItem(CART_STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }
    return parsed.map(normalizeCartRow).filter(Boolean);
  } catch (e) {
    // Corrupt JSON, or storage blocked by privacy settings.
    return [];
  }
};

/**
 * @param {Array<Object>} rows
 */
export const writeStoredCart = rows => {
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }

  const normalized = (Array.isArray(rows) ? rows : []).map(normalizeCartRow).filter(Boolean);

  try {
    storage.setItem(CART_STORAGE_KEY, JSON.stringify(normalized));
  } catch (e) {
    /* ignore quota / privacy errors */
  }
};

export const clearStoredCart = () => {
  const storage = getLocalStorage();
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(CART_STORAGE_KEY);
  } catch (e) {
    /* ignore */
  }
};
